// Общие элементы DOM-слоя (ux/01 общие элементы, ux/12 §5): полоса действия, навигация, шапка ресурсов,
// лист, окно подтверждения, тост, полосы офлайна, плитка предмета, скелетоны.
import type { ComponentChildren, JSX } from 'preact';
import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import { useApp } from '../app/app.js';
import type { ActionButton, ActionDesc } from '../nav/bottom-bar.js';
import { isBlocking, type SheetEntry, type Tab } from '../nav/navigator.js';
import { itemName, itemRequiredLevel, itemTooEarly } from '../content/catalog.js';
import type { Item } from '../content/types.js';
import { compact, timer } from '../i18n/format.js';
import { backOnline, online, player, serverNow, tutorialStep, wallet, walletAt } from '../state/game.js';
import { restartHint } from '../app/app.js';
import { hideToast, toast } from './toast.js';
import { I, type IconName } from '../assets/icons.js';
import { ICONS } from '../assets/icons.js';
import { Icon } from '../assets/ui.js';
import { t as tr } from '../i18n/t.js';
import { questsClaimable } from '../state/quests.js';
import { Count } from './text-fx.js';
import { bump } from './motion.js';

/** Тик раз в секунду для отсчётов (энергия, таймеры, техработы). */
export function useNow(periodMs = 1000): number {
  const [t, setT] = useState(() => serverNow());
  useEffect(() => {
    const id = setInterval(() => setT(serverNow()), periodMs);
    return () => clearInterval(id);
  }, [periodMs]);
  return t;
}

/** Экран кладёт описание полосы действия; при уходе снимает (ux/12 §5.1). */
export function useActionBar(desc: ActionDesc): void {
  const app = useApp();
  const id = useRef(0);
  useLayoutEffect(() => {
    id.current = app.bar.push(desc);
    return () => app.bar.remove(id.current);
  }, []);
  useLayoutEffect(() => { app.bar.update(id.current, desc); });
}

export function Btn(p: { b: ActionButton; cls?: string; inSheet?: boolean }) {
  const app = useApp();
  void app.bar.version.value;
  const pending = app.bar.isPending(p.b);
  const tone = p.b.tone ?? 'accent';
  // Кнопка стала доступной (итог боя через 2.5 с, «Забрать» после ответа) — один короткий толчок.
  const ref = useRef<HTMLButtonElement>(null);
  const wasOff = useRef(!!p.b.disabled);
  useLayoutEffect(() => {
    const off = !!p.b.disabled;
    if (wasOff.current && !off) bump(ref.current, 1.05);
    wasOff.current = off;
  });
  return (
    <button
      ref={ref}
      class={`btn ${tone} ${p.cls ?? ''}`}
      disabled={p.b.disabled || pending}
      data-testid={p.b.testId}
      data-tut={p.b.testId}
      aria-busy={pending}
      // Главное действие отвечает лёгкой вибрацией сразу; своя вибрация обработчика (medium, success) — сильнее и проходит (ux/12 §7).
      onClick={() => { if (tone === 'accent') app.haptic('light'); void app.bar.press(p.b); }}
    >
      {pending ? <span class="spinner" aria-hidden /> : null}
      {p.b.portrait && !pending ? <span class="btn-portrait"><Icon id={p.b.portrait} fallback={ICONS.boss[1]} size={42} /></span> : null}
      {p.b.text}
      {p.b.icon ? <I n={p.b.icon} size={18} /> : null}
    </button>
  );
}

export function ActionBar(p: { last: boolean }) {
  const app = useApp();
  void app.bar.version.value;
  const d = app.bar.current();
  if (!d || d.hidden || (!d.main && !d.secondary && !d.back)) return null;
  return (
    <div class={`action-bar ${p.last ? 'last' : ''}`} data-testid="action-bar">
      {d.back ? <button class="btn outline back" aria-label={tr('UI-BTN-BACK')} onClick={() => app.nav.back()}>‹</button> : null}
      {d.secondary ? <Btn b={{ tone: 'outline', ...d.secondary }} /> : null}
      {d.main ? <Btn b={d.main} cls="main" /> : null}
    </div>
  );
}

/** label — ключ строки: подпись берётся при отрисовке, чтобы перекрытие пакета применялось сразу. */
const TABS: { tab: Tab; id: string; label: string; icon: IconName }[] = [
  { tab: 'home', id: 'UI-NAV-HOME', label: 'UI-NAV-HOME', icon: 'navHome' },
  { tab: 'map', id: 'UI-NAV-MAP', label: 'UI-NAV-MAP', icon: 'navMap' },
  { tab: 'coop', id: 'UI-NAV-COOP', label: 'UI-NAV-COOP', icon: 'navCoop' },
  { tab: 'quests', id: 'UI-NAV-QUESTS', label: 'UI-NAV-QUESTS', icon: 'navQuests' },
  { tab: 'shop', id: 'UI-NAV-SHOP', label: 'UI-NAV-SHOP', icon: 'navShop' },
];

export function NavBar(p: { side?: boolean }) {
  const app = useApp();
  void app.navVersion.value;
  const s = app.nav.state;
  return (
    <nav class={p.side ? 'side-nav' : 'nav-bar'} data-testid="nav-bar">
      <div class={p.side ? '' : 'inner'} style={p.side ? { display: 'contents' } : undefined}>
        {TABS.map((t) => (
          <button
            key={t.tab}
            class={`nav-item ${s.tab === t.tab ? 'active' : ''}`}
            data-testid={t.id}
            aria-current={s.tab === t.tab ? 'page' : undefined}
            onClick={() => { app.haptic('selection'); app.nav.selectTab(t.tab); }}
          >
            <span class="nav-ico"><I n={t.icon} size={28} />{t.tab === 'quests' && questsClaimable.value ? <span class="nav-badge" data-testid="badge-quests">{questsClaimable.value > 9 ? '9+' : questsClaimable.value}</span> : null}</span>
            <span>{tr(t.label)}</span>
          </button>
        ))}
      </div>
    </nav>
  );
}

/** Аватар 44 pt с кольцом опыта и уровнем; точка «!» — есть свободные очки (ux/03 §1.2). Тап → SCR-CHAR. */
export function Avatar() {
  const app = useApp();
  const p = player.value;
  const frac = p && p.xpToNext ? Math.min(1, p.xp / p.xpToNext) : p ? 1 : 0;
  const c = 2 * Math.PI * 19;
  return (
    <button class="avatar" data-testid="home-char" aria-label={p ? tr('CL-UI-KIT-01', { p0: p.level, p1: p.statPointsFree ? tr('CL-UI-KIT-POINTS') : '' }) : tr('CL-UI-KIT-02')} onClick={() => { app.haptic('light'); app.nav.push('SCR-CHAR'); }}>
      <svg viewBox="0 0 44 44" width="44" height="44" aria-hidden>
        <circle cx="22" cy="22" r="19" fill="var(--surface)" stroke="var(--surface-2)" stroke-width="4" />
        <circle cx="22" cy="22" r="19" fill="none" stroke="var(--accent)" stroke-width="4" stroke-linecap="round" stroke-dasharray={`${c * frac} ${c}`} transform="rotate(-90 22 22)" />
      </svg>
      <span class="avatar-lvl">{p?.level ?? '—'}</span>
      {p?.statPointsFree ? <span class="avatar-dot" data-testid="avatar-dot" aria-hidden>!</span> : null}
    </button>
  );
}

/** Шапка ресурсов SCR-HOME и L1: аватар, энергия, гайки, талоны (E06-T04); тап → MOD-RESOURCE-INFO / MOD-ENERGY. */
export function ResourceHeader(p: { avatar?: boolean; right?: ComponentChildren } = {}) {
  const app = useApp();
  const w = wallet.value;
  const now = useNow();
  const e = w?.energy;
  const next = e?.nextAt ? Date.parse(e.nextAt) - now : 0;
  return (
    <header class="res-header" data-testid="res-header">
      {p.avatar ? <Avatar /> : null}
      <button class="res-pill" data-testid="res-energy" aria-label={tr('CL-UI-KIT-03')} onClick={() => app.nav.openSheet('MOD-ENERGY')}>
        <I n="energy" size={22} />{e ? <span><Count value={e.value} format={String} />/{e.max}</span> : '—'}
        {e && e.value < e.max && next > 0 ? <small>{timer(next)}</small> : null}
      </button>
      <button class="res-pill" data-testid="res-nuts" aria-label={tr('UI-RES-NUTS')} onClick={() => app.nav.openSheet('MOD-RESOURCE-INFO', { currency: 'nuts' })}>
        <I n="nuts" size={22} />{w ? <Count value={w.nuts} format={compact} testId="res-nuts-value" /> : '—'}
      </button>
      <span class="res-pill res-split">
        <button data-testid="res-talons" aria-label={tr('SHOP-TAB-TALONS')} onClick={() => app.nav.openSheet('MOD-RESOURCE-INFO', { currency: 'talons' })}>
          <I n="talons" size={22} /><span class={w && w.talons < 0 ? 'neg' : ''}>{w ? <Count value={w.talons} format={compact} bump=".res-split" testId="res-talons-value" /> : '—'}</span>
        </button>
        {/* «+» — отдельная тач-цель 44 × 44: Ларёк гостевым листом, без смены вкладки (ux/03 §1.2, ux/02 §2.1 п. 2) */}
        <button class="res-plus" data-testid="res-talons-plus" aria-label={tr('CL-UI-KIT-04')} onClick={() => { app.haptic('light'); app.nav.openSheet('SCR-STORE', {}, { guest: true }); }}>+</button>
      </span>
      <span class="spacer" />
      {p.right}
      {!p.avatar && player.value ? <span class="lvl">{tr('CL-UI-KIT-05')}{" "}{player.value.level}</span> : null}
    </header>
  );
}

export function ScreenTitle(p: { children: ComponentChildren; right?: ComponentChildren }) {
  return <div class="screen-title with-safe" role="heading" aria-level={1}><span style={{ flex: 1 }}>{p.children}</span>{p.right}</div>;
}

/** Полосы под шапкой: офлайн / связь есть / «Перезапустить игру?» (ux/11 §4). */
export function Strips() {
  const at = walletAt.value;
  const shown = !online.value || backOnline.value || restartHint.value;
  useEffect(() => { document.documentElement.dataset.strip = shown ? '1' : '0'; }, [shown]);
  if (!online.value) {
    const t = at ? new Date(at).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }) : '';
    return <div class="strip offline" role="status" data-testid="offline">{tr('CL-UI-KIT-06')}{t ? tr('CL-UI-KIT-07', { p0: t }) : ''}</div>;
  }
  if (backOnline.value) return <div class="strip online" role="status">{tr('CL-UI-KIT-08')}</div>;
  if (restartHint.value) {
    return (
      <div class="strip restart" role="status">
        
        {tr('CL-UI-KIT-09')}
        <button onClick={() => location.reload()}>{tr('CL-UI-KIT-10')}</button>
        <button onClick={() => { restartHint.value = false; }} aria-label={tr('CL-UI-KIT-11')}><I n="close" size={16} /></button>
      </div>
    );
  }
  return null;
}

export function ToastView() {
  const t = toast.value;
  if (!t) return null;
  return (
    <div class={`toast ${t.kind}`} role="status" aria-live="polite" data-testid="toast">
      <span class="t">{t.text}</span>
      {t.action ? <button onClick={() => { t.action!.onClick(); hideToast(); }}>{t.action.text}</button> : null}
    </div>
  );
}

/**
 * Нижний лист (ux/12 §4): тап по затемнению закрывает только окна без ⛔. Кнопки окна — в его нижней половине.
 * На клиентах ниже Bot API 7.7 закрытие свайпом выключено — показываем «✕» в нижней половине справа.
 */
export function Sheet(p: { entry: SheetEntry; title?: ComponentChildren; children: ComponentChildren; actions?: ActionButton[]; full?: boolean; testId?: string; onDismiss?: () => boolean }) {
  const app = useApp();
  const blocking = isBlocking(p.entry.id);
  // onDismiss → true: окно само обработало тап мимо (например, «Отменить» при открытом счёте) и не закрывается.
  const close = () => { if (p.onDismiss?.()) return; if (!blocking) app.nav.popSheet(); };
  return (
    <div class="overlay" onClick={(e) => { if (e.target === e.currentTarget) close(); }} data-testid="overlay">
      <section class={`sheet ${p.full ? 'full' : ''}`} role="dialog" aria-modal="true" aria-label={typeof p.title === 'string' ? p.title : p.entry.id} data-testid={p.testId ?? p.entry.id}>
        <div class="handle" aria-hidden />
        <div class="body">
          {p.title ? <h2 class="sheet-title">{p.title}</h2> : null}
          {p.children}
        </div>
        {p.actions?.length ? (
          <div class="actions">
            {p.actions.map((b) => <Btn key={b.testId ?? b.text} b={b} />)}
          </div>
        ) : null}
      </section>
    </div>
  );
}

/** Окно по центру — MOD-CONFIRM и ⛔-подтверждения: не закрывается тапом мимо (ux/11 §5). */
export function Dialog(p: { title: string; text?: ComponentChildren; children?: ComponentChildren; actions: ActionButton[]; testId: string }) {
  return (
    <div class="overlay center" data-testid="overlay-blocking">
      <section class="dialog" role="alertdialog" aria-modal="true" aria-label={p.title} data-testid={p.testId}>
        <h2>{p.title}</h2>
        {p.text ? <p>{p.text}</p> : null}
        {p.children}
        <div class="actions">{p.actions.map((b) => <Btn key={b.testId ?? b.text} b={b} />)}</div>
      </section>
    </div>
  );
}

/** Кнопка рискованного действия активна через 1.5 с (эпические и легендарные, ux/05 §5, А1-055). */
export function useArmDelay(ms: number): boolean {
  const [armed, setArmed] = useState(ms <= 0);
  useEffect(() => {
    if (ms <= 0) return;
    const t = setTimeout(() => setArmed(true), ms);
    return () => clearTimeout(t);
  }, [ms]);
  return armed;
}

/** Значок слота: иконка ICO-SLOT-* или запасной символ. */
export const slotGlyph = (s: Item['slot'], size = 26) => <I n={s} size={size} />;

export function ItemTile(p: { item: Item; onClick?: () => void; selected?: boolean; testId?: string } & Omit<JSX.HTMLAttributes<HTMLButtonElement>, 'onClick'>) {
  const { item, onClick, selected, testId, ...rest } = p;
  // «Рано» (Р-107): предмет требует уровня выше игрока — плашка «с ур. N» и приглушённый вид.
  const early = itemTooEarly(item, player.value?.level);
  const need = itemRequiredLevel(item);
  return (
    <button
      {...rest}
      class={`item-tile rarity-${item.rarity} ${selected ? 'selected' : ''} ${early ? 'early' : ''}`}
      onClick={onClick}
      data-testid={testId ?? `item-${item.itemId}`}
      data-early={early ? '1' : undefined}
      aria-label={`${itemName(item.defId)}${item.upgradeLevel ? ` +${item.upgradeLevel}` : ''}${item.equipped ? tr('CL-UI-KIT-12') : ''}${early ? `, ${tr('CL-ITEM-FROM-LVL', { n: need })}` : ''}`}
    >
      {early ? <span class="req" data-testid={`item-${item.itemId}-req`}>{tr('CL-ITEM-FROM-LVL', { n: need })}</span> : null}
      {item.equipped ? <span class="eq">✓</span> : null}
      {item.upgradeLevel ? <span class="lvl">+{item.upgradeLevel}</span> : null}
      {item.locked ? <span class="lock"><I n="lock" size={12} /></span> : null}
      <span class="glyph" aria-hidden><Icon id={item.defId} fallback={ICONS[item.slot][1]} size={36} /></span>
      <span class="nm">{itemName(item.defId)}</span>
    </button>
  );
}

export function Skeleton(p: { h: number; n?: number }) {
  return <div class="col" aria-busy="true" aria-label={tr('CL-UI-KIT-13')}>{Array.from({ length: p.n ?? 1 }, (_, i) => <div key={i} class="skeleton" style={{ height: p.h }} />)}</div>;
}

export function ErrorBanner(p: { what: string; onRetry: () => void }) {
  return (
    <div class="banner" role="alert" data-testid="st-error">
      <span style={{ flex: 1 }}>{p.what} {" "}{tr('CL-UI-KIT-14')}</span>
      <button class="btn small outline" onClick={p.onRetry}>{tr('CL-UI-KIT-15')}</button>
    </div>
  );
}

export function tutorialLocked(): boolean { return /^T0[1-6]$/.test(tutorialStep.value); }
